import React from 'react';
import { DateTime } from 'luxon';
import { Ticker } from '@benzinga/session';
import type { SearchResultItem } from '@benzinga/basic-news-manager';
import { formatTickerToURLFriendly } from '@benzinga/utils';
import { CHIP_CATEGORIES, getSearchResultCategory } from './article-category';
import { CategoryIcon } from './CategoryIcon';
import { highlightMatch } from './highlightMatch';
import { FallbackLogoSVG } from './FallbackLogoSVG';
import styles from './Search.module.scss';

export interface AutocompleteResultsProps {
  foundedTickers: Ticker[];
  foundedArticles: SearchResultItem[];
  searchQuery: string;
  selectElementListClass: string;
  selectedIndex: number;
  onTickerClick: (ticker: Ticker) => void;
  onKeyDown: React.KeyboardEventHandler<HTMLAnchorElement>;
  showKeywordsSearch: boolean;
}

const formatShortDate = (date: string | undefined): string => {
  if (!date) return '';
  const dt = DateTime.fromISO(date);
  return dt.isValid ? dt.toFormat('LLL d') : '';
};

const pluralize = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

const quoteLink = (symbol: string) => `/quote/${formatTickerToURLFriendly(symbol)}`;

export const AutocompleteResults: React.FC<AutocompleteResultsProps> = ({
  foundedArticles,
  foundedTickers,
  onKeyDown,
  onTickerClick,
  searchQuery,
  selectedIndex,
  selectElementListClass,
  showKeywordsSearch,
}) => {
  const activeClass = (flatIndex: number) => `${selectElementListClass}${flatIndex === selectedIndex ? ' active' : ''}`;

  const articleOffset = foundedTickers.length;
  const footerIndex = foundedTickers.length + foundedArticles.length;

  return (
    <>
      {foundedTickers.length > 0 && (
        <div className="search-results-section">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionHeaderLabel}>Symbols</span>
            <span className={styles.sectionHeaderCount}>{pluralize(foundedTickers.length, 'match', 'matches')}</span>
          </div>
          <div className={`${styles.resultsWrapper} search-results-wrapper`}>
            {foundedTickers.map((ticker, idx) => (
              <a
                className={`${styles.symbolRow} ${activeClass(idx)}`}
                href={quoteLink(ticker.symbol)}
                key={ticker.symbol}
                onClick={event => {
                  event.preventDefault();
                  onTickerClick(ticker);
                }}
                onKeyDown={onKeyDown}
                tabIndex={-1}
              >
                {ticker.logo ? (
                  <span className={styles.symbolRowLogo} style={{ backgroundImage: `url(${ticker.logo})` }} />
                ) : (
                  <FallbackLogoSVG letter={ticker.symbol?.charAt(0) ?? ''} />
                )}
                <span className={styles.symbolRowBody}>
                  <span className={styles.symbolRowTitle}>{highlightMatch(ticker.symbol, searchQuery)}</span>
                  {ticker.name ? (
                    <span className={styles.symbolRowName}>{highlightMatch(ticker.name, searchQuery)}</span>
                  ) : null}
                </span>
                {ticker.exchange ? <span className={styles.symbolRowExchange}>{ticker.exchange}</span> : null}
              </a>
            ))}
          </div>
        </div>
      )}

      {foundedArticles.length > 0 && (
        <div className="search-results-section">
          <div className={styles.sectionHeader}>
            <span className={styles.sectionHeaderLabel}>Articles</span>
            <span className={styles.sectionHeaderCount}>{pluralize(foundedArticles.length, 'result', 'results')}</span>
          </div>
          <div className={`${styles.resultsWrapper} search-results-wrapper`}>
            {foundedArticles.map((article, idx) => {
              const category = getSearchResultCategory(article);
              const showChip = CHIP_CATEGORIES.has(category);
              const shortDate = formatShortDate(article.CreatedAt);
              return (
                <a
                  className={`${styles.articleRow} ${activeClass(articleOffset + idx)}`}
                  href={article.ArticleURL}
                  key={String(article.NodeID ?? `${article.ArticleURL}-${idx}`)}
                  onKeyDown={onKeyDown}
                  tabIndex={-1}
                >
                  <span className={styles.articleRowBody}>
                    <span className={styles.articleRowTitle}>{highlightMatch(article.Title, searchQuery)}</span>
                    <span className={styles.articleRowMeta}>
                      {shortDate ? <span className={styles.shortDate}>{shortDate}</span> : null}
                      {showChip ? (
                        <span className={styles.categoryChip}>
                          <CategoryIcon category={category} className={styles.categoryChipIcon} />
                          {category}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {showKeywordsSearch && (
        <a
          className={`${styles.footerCta} ${activeClass(footerIndex)}`}
          href={`/search?q=${encodeURIComponent(searchQuery)}`}
          onKeyDown={onKeyDown}
          tabIndex={-1}
        >
          <span className={styles.footerCtaLabel}>
            <svg
              aria-hidden
              fill="none"
              height={16}
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 16 16"
              width={16}
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx={7} cy={7} r={4.5} />
              <path d="M11 11l3 3" strokeLinecap="round" />
            </svg>
            Search &quot;{searchQuery}&quot; to view all results
          </span>
          <span aria-hidden>→</span>
        </a>
      )}
    </>
  );
};
